import React, { useState } from 'react';
import { X } from 'lucide-react';
import { addDevice, addBlocker, addNote, updateAccount } from '../../api';
import { toast } from 'sonner';

export const DeviceModal = ({ onClose, onSuccess, accountId }) => {
  const [formData, setFormData] = useState({ device_type: 'Laptop', quantity: 1 });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDevice(accountId, formData);
      toast.success('Device added');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add device');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-surface border border-border rounded-lg shadow-xl" data-testid="device-modal">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Add Device</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-highlight text-text-secondary"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Device Type</label>
            <select
              value={formData.device_type}
              onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="device-type-select"
            >
              <option value="Desktop">Desktop</option>
              <option value="Laptop">Laptop</option>
              <option value="Tablet">Tablet</option>
              <option value="Phone">Phone</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Quantity</label>
            <input
              type="number"
              min="1"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="device-quantity-input"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-surface-highlight text-text-primary rounded-md">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md disabled:opacity-50" data-testid="submit-device-btn">
              {loading ? 'Adding...' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BLOCKER_OPTIONS = {
  Desktop: ['INTUNE', 'COMPUTRACE', 'BIOS PASSWORD', 'MDM LOCKED'],
  Laptop: ['INTUNE', 'COMPUTRACE', 'BIOS PASSWORD', 'MDM LOCKED'],
  Tablet: ['INTUNE', 'COMPUTRACE', 'BIOS PASSWORD', 'MDM LOCKED'],
  Phone: ['MDM LOCKED', 'GOOGLE LOCKED']
};

export const BlockerModal = ({ onClose, onSuccess, accountId, devices }) => {
  const [formData, setFormData] = useState({
    device_type: devices[0]?.device_type || 'Laptop',
    blocker_type: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.blocker_type) {
      toast.error('Please select a blocker type');
      return;
    }
    setLoading(true);
    try {
      await addBlocker(accountId, formData);
      toast.success('Blocker flagged');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add blocker');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-surface border border-border rounded-lg shadow-xl" data-testid="blocker-modal">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Flag Blocker</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-highlight text-text-secondary"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Device Type</label>
            <select
              value={formData.device_type}
              onChange={(e) => setFormData({ device_type: e.target.value, blocker_type: '' })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="blocker-device-select"
            >
              {devices.map(d => (
                <option key={d.device_type} value={d.device_type}>{d.device_type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Blocker Type</label>
            <select
              value={formData.blocker_type}
              onChange={(e) => setFormData({ ...formData, blocker_type: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="blocker-type-select"
            >
              <option value="">Select blocker type</option>
              {BLOCKER_OPTIONS[formData.device_type]?.map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-surface-highlight text-text-primary rounded-md">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-destructive text-white rounded-md disabled:opacity-50" data-testid="submit-blocker-btn">
              {loading ? 'Adding...' : 'Flag Blocker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const NoteModal = ({ onClose, onSuccess, accountId }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setLoading(true);
    try {
      await addNote(accountId, content);
      toast.success('Note added');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-surface border border-border rounded-lg shadow-xl" data-testid="note-modal">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Add Note</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-highlight text-text-secondary"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Note</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Enter your note..."
              data-testid="note-content-input"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-surface-highlight text-text-primary rounded-md">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md disabled:opacity-50" data-testid="submit-note-btn">
              {loading ? 'Adding...' : 'Add Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const EditAccountModal = ({ account, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    account_number: account.account_number || '',
    account_name: account.account_name || '',
    account_received_date: account.account_received_date ? account.account_received_date.split('T')[0] : ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.account_number.trim() || !formData.account_name.trim()) {
      toast.error('Account number and name are required');
      return;
    }
    setLoading(true);
    try {
      await updateAccount(account.id, formData);
      toast.success('Account updated');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-surface border border-border rounded-lg shadow-xl" data-testid="edit-account-modal">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Edit Account</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-highlight text-text-secondary"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Account Number</label>
            <input
              type="text"
              value={formData.account_number}
              onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
              required
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="edit-account-number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Account Name</label>
            <input
              type="text"
              value={formData.account_name}
              onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
              required
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="edit-account-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Date Received</label>
            <input
              type="date"
              value={formData.account_received_date}
              onChange={(e) => setFormData({ ...formData, account_received_date: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="edit-account-date"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-surface-highlight text-text-primary rounded-md font-medium hover:bg-border transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors disabled:opacity-50" data-testid="save-account-btn">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
